import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import {
  FiBell,
  FiChevronDown,
  FiCreditCard,
  FiLogOut,
  FiMenu,
  FiSettings,
  FiShield,
  FiStar,
  FiTarget,
  FiUser,
  FiUsers,
  FiX,
} from "react-icons/fi";
import { User, Notification } from "../../types";
import { useAuthStore } from "../../store/authStore";
import { useNotificationStore } from "../../store/notificationStore";
import { useLanguage } from "../../contexts/LanguageContext";
import { useDismiss } from "../../hooks/useDismiss";
import { menuKeys } from "../../hooks/menuKeys";
import LanguageMenu from "../UI/LanguageMenu";

const Bar = styled.header`
  position: sticky;
  top: 0;
  z-index: ${({ theme }) => theme.zIndex.sticky};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  height: ${({ theme }) => theme.layout.header};
  padding: 0 clamp(16px, 2.5vw, 32px);
  background: ${({ theme }) => theme.colors.background};
  @media (max-width: 767px) {
    height: ${({ theme }) => theme.layout.headerMobile};
    padding: 0 8px 0 4px;
  }
`;
const Right = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 4px;
`;
const IconBtn = styled.button`
  position: relative;
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  border-radius: 10px;
  color: ${({ theme }) => theme.colors.textSecondary};
  transition:
    background ${({ theme }) => theme.motion.fast},
    color ${({ theme }) => theme.motion.fast};
  &:hover,
  &[aria-expanded="true"] {
    background: ${({ theme }) => theme.colors.raised};
    color: ${({ theme }) => theme.colors.text};
  }
  svg {
    width: 20px;
    height: 20px;
  }
`;
const MenuToggle = styled(IconBtn)`
  display: none;
  @media (max-width: 767px) {
    display: grid;
  }
`;
const Badge = styled.span`
  position: absolute;
  top: 6px;
  right: 4px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.onPrimary};
  border: 2px solid ${({ theme }) => theme.colors.background};
  box-sizing: content-box;
  font-size: 11px;
  font-weight: 700;
  line-height: 18px;
  text-align: center;
`;
const Pop = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  z-index: ${({ theme }) => theme.zIndex.dropdown};
  border-radius: 12px;
  background: ${({ theme }) => theme.colors.raised};
  border: 1px solid ${({ theme }) => theme.colors.border};
  box-shadow: 0 16px 40px #0009;
`;
const Anchor = styled.div`
  position: relative;
`;
const Avatar = styled.span`
  display: grid;
  place-items: center;
  width: 32px;
  height: 32px;
  flex: 0 0 32px;
  overflow: hidden;
  border-radius: 10px;
  background: ${({ theme }) => theme.colors.surfaceHover};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 13px;
  font-weight: 650;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;
const AccountBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  height: 44px;
  padding: 0 8px 0 6px;
  border-radius: 10px;
  color: ${({ theme }) => theme.colors.text};
  font-size: 14px;
  font-weight: 600;
  transition: background ${({ theme }) => theme.motion.fast};
  &:hover,
  &[aria-expanded="true"] {
    background: ${({ theme }) => theme.colors.raised};
  }
  .name {
    max-width: 140px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  > svg {
    width: 16px;
    height: 16px;
    color: ${({ theme }) => theme.colors.textMuted};
  }
  @media (max-width: 767px) {
    .name {
      display: none;
    }
  }
`;
const AccountMenu = styled(Pop)`
  min-width: 220px;
  padding: 6px;
  button {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 12px;
    min-height: 44px;
    padding: 0 12px;
    border-radius: 8px;
    color: ${({ theme }) => theme.colors.text};
    font-size: 14px;
    font-weight: 500;
    text-align: left;
    svg {
      width: 18px;
      height: 18px;
      color: ${({ theme }) => theme.colors.textSecondary};
    }
    &:hover,
    &:focus-visible {
      background: ${({ theme }) => theme.colors.surfaceHover};
    }
  }
  hr {
    border: 0;
    height: 1px;
    margin: 4px 8px;
    background: ${({ theme }) => theme.colors.border};
  }
  .who {
    padding: 8px 12px 10px;
    font-size: 13px;
    line-height: 18px;
    color: ${({ theme }) => theme.colors.textMuted};
    strong {
      display: block;
      color: ${({ theme }) => theme.colors.text};
      font-size: 14px;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
`;
const Notif = styled(Pop)`
  width: 380px;
  max-height: min(480px, calc(100svh - 96px));
  display: flex;
  flex-direction: column;
  overflow: hidden;
  @media (max-width: 767px) {
    position: fixed;
    top: ${({ theme }) => theme.layout.headerMobile};
    left: 8px;
    right: 8px;
    width: auto;
  }
  header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 8px 8px 8px 16px;
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    h2 {
      font-size: 16px;
      font-weight: 600;
    }
  }
  .text-btn {
    min-height: 36px;
    padding: 0 10px;
    border-radius: 8px;
    color: ${({ theme }) => theme.colors.primary};
    font-size: 13px;
    font-weight: 600;
    &:hover {
      background: ${({ theme }) => theme.colors.primarySoft};
    }
  }
  .list {
    overflow-y: auto;
    overscroll-behavior: contain;
  }
  .empty {
    padding: 24px 16px;
    text-align: center;
    font-size: 14px;
    color: ${({ theme }) => theme.colors.textMuted};
  }
`;
const Row = styled.div<{ $unread: boolean }>`
  display: grid;
  grid-template-columns: 32px minmax(0, 1fr) auto;
  gap: 12px;
  align-items: start;
  padding: 12px 8px 12px 16px;
  background: ${({ $unread, theme }) => ($unread ? theme.colors.primarySoft : "transparent")};
  & + & {
    border-top: 1px solid ${({ theme }) => theme.colors.border};
  }
  .icon {
    display: grid;
    place-items: center;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: ${({ theme }) => theme.colors.surfaceHover};
    color: ${({ theme }) => theme.colors.textSecondary};
    svg {
      width: 16px;
      height: 16px;
    }
  }
  .body {
    min-width: 0;
    text-align: left;
    strong {
      display: block;
      font-size: 14px;
      line-height: 20px;
    }
    span {
      display: block;
      font-size: 13px;
      line-height: 18px;
      color: ${({ theme }) => theme.colors.textSecondary};
    }
    time {
      font-size: 12px;
      color: ${({ theme }) => theme.colors.textMuted};
    }
  }
  button.main {
    text-align: left;
    border-radius: 8px;
    min-width: 0;
  }
`;

const ICONS: Record<string, React.ReactNode> = {
  friend_request: <FiUsers />,
  match_found: <FiTarget />,
  clan_invite: <FiUsers />,
  subscription: <FiCreditCard />,
  system: <FiShield />,
};

export default function Header({
  user,
  onToggleSidebar,
  menuButtonRef,
}: {
  user: User | null;
  onToggleSidebar: () => void;
  menuButtonRef?: React.Ref<HTMLButtonElement>;
}) {
  const nav = useNavigate();
  const { pathname } = useLocation();
  const { t } = useLanguage();
  const { logout } = useAuthStore();
  const n = useNotificationStore();
  const [account, setAccount] = useState(false);
  const accRef = useRef<HTMLDivElement>(null);
  const accBtn = useRef<HTMLButtonElement>(null);
  const noteRef = useRef<HTMLDivElement>(null);
  const noteBtn = useRef<HTMLButtonElement>(null);
  const closeAcc = useCallback(() => setAccount(false), []);
  useDismiss(account, closeAcc, accRef, accBtn);
  useDismiss(n.isDropdownOpen, n.closeDropdown, noteRef, noteBtn);
  useEffect(() => {
    setAccount(false);
    n.closeDropdown();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const go = (path: string) => {
    closeAcc();
    nav(path);
  };
  const time = (iso: string) => {
    const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
    if (m < 1) return t("time.just_now");
    if (m < 60) return t("time.minutes_ago", { value: m });
    if (m < 1440) return t("time.hours_ago", { value: Math.floor(m / 60) });
    return new Date(iso).toLocaleDateString(t("time.locale"));
  };
  const open = (x: Notification) => {
    if (!x.isRead) n.markAsRead(x.id);
    if (x.actionUrl) {
      nav(x.actionUrl);
      n.closeDropdown();
    }
  };

  return (
    <Bar>
      <MenuToggle ref={menuButtonRef} type="button" aria-label={t("common.open_menu")} onClick={onToggleSidebar}>
        <FiMenu aria-hidden="true" />
      </MenuToggle>
      <Right>
        <LanguageMenu />
        <Anchor ref={noteRef}>
          <IconBtn
            ref={noteBtn}
            type="button"
            aria-haspopup="dialog"
            aria-expanded={n.isDropdownOpen}
            aria-label={n.unreadCount ? `${t("header.notifications")}: ${n.unreadCount}` : t("header.notifications")}
            onClick={() => {
              setAccount(false);
              n.toggleDropdown();
            }}
          >
            <FiBell aria-hidden="true" />
            {n.unreadCount > 0 && <Badge aria-hidden="true">{n.unreadCount > 99 ? "99+" : n.unreadCount}</Badge>}
          </IconBtn>
          {n.isDropdownOpen && (
            <Notif role="dialog" aria-label={t("header.notifications")}>
              <header>
                <h2>{t("header.notifications")}</h2>
                <span>
                  {n.unreadCount > 0 && (
                    <button type="button" className="text-btn" onClick={n.markAllAsRead}>
                      {t("header.markAllRead")}
                    </button>
                  )}
                  <IconBtn type="button" style={{ width: 36, height: 36, display: "inline-grid" }} aria-label={t("header.close_notifications")} onClick={() => { n.closeDropdown(); noteBtn.current?.focus(); }}>
                    <FiX aria-hidden="true" />
                  </IconBtn>
                </span>
              </header>
              <div className="list">
                {n.notifications.length === 0 ? (
                  <p className="empty">{t("header.noNotifications")}</p>
                ) : (
                  n.notifications.map((x) => (
                    <Row key={x.id} $unread={!x.isRead}>
                      <span className="icon" aria-hidden="true">{ICONS[x.type] || <FiBell />}</span>
                      <button type="button" className="main body" onClick={() => open(x)}>
                        <strong>{x.title}</strong>
                        <span>{x.message}</span>
                        <time dateTime={x.createdAt}>{time(x.createdAt)}</time>
                      </button>
                      <IconBtn type="button" style={{ width: 36, height: 36 }} aria-label={t("header.delete_notification")} onClick={() => n.removeNotification(x.id)}>
                        <FiX aria-hidden="true" />
                      </IconBtn>
                    </Row>
                  ))
                )}
              </div>
            </Notif>
          )}
        </Anchor>
        <Anchor ref={accRef}>
          <AccountBtn
            ref={accBtn}
            type="button"
            aria-haspopup="menu"
            aria-expanded={account}
            aria-label={`${t("header.account_menu")}: ${user?.username || ""}`}
            onClick={() => {
              n.closeDropdown();
              setAccount((v) => !v);
            }}
          >
            <Avatar aria-hidden="true">
              {user?.avatar ? <img src={user.avatar} alt="" /> : (user?.username || "?").slice(0, 1).toUpperCase()}
            </Avatar>
            <span className="name">{user?.username || t("header.userDefault")}</span>
            <FiChevronDown aria-hidden="true" />
          </AccountBtn>
          {account && (
            <AccountMenu
              role="menu"
              aria-label={t("header.account_menu")}
              onKeyDown={menuKeys}
              ref={(el) => el?.querySelector<HTMLElement>('[role="menuitem"]')?.focus()}
            >
              <div className="who">
                <strong>{user?.username}</strong>
                {user?.email}
              </div>
              <hr />
              <button type="button" role="menuitem" onClick={() => go("/profile")}>
                <FiUser aria-hidden="true" />
                {t("header.profile")}
              </button>
              <button type="button" role="menuitem" onClick={() => go("/settings")}>
                <FiSettings aria-hidden="true" />
                {t("header.settings")}
              </button>
              <button type="button" role="menuitem" onClick={() => go("/subscription")}>
                <FiStar aria-hidden="true" />
                {t("header.subscription")}
              </button>
              <hr />
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  logout();
                  toast.success(t("header.logout_success"));
                  nav("/login");
                }}
              >
                <FiLogOut aria-hidden="true" />
                {t("header.logout")}
              </button>
            </AccountMenu>
          )}
        </Anchor>
      </Right>
    </Bar>
  );
}
